<template>
  <div class="sm-notification-item">
    <div class="sm-notification-item__icon">
      <slot name="icon">
        <component :is="icon" :class="classes" />
      </slot>
    </div>
    <div class="sm-notification-item__content">
      <div class="sm-notification-item__content__title">
        <slot name="title">
          <h4>{{ props.title }}</h4>
        </slot>
      </div>
      <div class="sm-notification-item__content__description">
        <slot name="description">{{ props.message }}</slot>
      </div>

      <!-- Progress bar -->
      <div v-if="showProgressBar" class="sm-notification-item__content__progress group w-full">
        <div class="sm-notification-progress-bar">
          <div class="sm-notification-progress-bar__fill" :style="{ width: progressPercentage + '%' }"></div>
        </div>
        <div class="sm-notification-progress-bar__text flex">
          {{ progressPercentage }}%
          <div class="ml-2 hidden group-hover:block">
            ({{ props.currentProgress }}/{{ props.totalProgress }})
          </div>
        </div>
      </div>
    </div>

    <!-- Action -->
    <div class="sm-notification-item__action">
      <slot name="action">
        <div class="sm-notification-close">
          <SmButtonClose @close="close" />
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import SmButtonClose from '@/components/sm/SmButtonClose.vue'
import {
  CircleAlert,
  CircleCheck,
  CircleX,
  Info,
  Loader,
} from 'lucide-vue-next'

const props = defineProps({
  type: {
    type: String,
    default: 'success',
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  duration: {
    type: Number,
    default: 2000,
  },
  currentProgress: {
    type: Number,
    default: undefined,
  },
  totalProgress: {
    type: Number,
    default: undefined,
  },
  selfClose: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['close'])

const close = () => emit('close')

const showProgressBar = computed(() => {
  return (
    props.currentProgress !== undefined && props.totalProgress !== undefined
  )
})

const progressPercentage = computed(() => {
  if (!showProgressBar.value) return 0
  return Math.round(
    ((props.currentProgress || 0) / (props.totalProgress || 1)) * 100
  )
})

const icon = computed(() => {
  switch (props.type) {
    case 'success':
      return CircleCheck
    case 'error':
      return CircleX
    case 'warning':
      return CircleAlert
    case 'info':
      return Info
    case 'loading':
      return Loader
    default:
      return CircleCheck
  }
})

const classes = computed(() => {
  switch (props.type) {
    case 'success':
      return 'text-green'
    case 'error':
      return 'text-magenta'
    case 'warning':
      return 'text-orange'
    case 'info':
      return 'text-blue'
    case 'loading':
      return 'text-blue animation-spin'
    default:
      return 'text-primary'
  }
})

onMounted(() => {
  if (!props.selfClose) return
  // Do not close if loading
  if (props.type === 'loading') return
  // Close after duration
  initSelfClose()
})

// If type changes init self close
watch(
  () => props.type,
  () => {
    if (!props.selfClose) return
    // Do not close if loading
    if (props.type === 'loading') return
    // Close after duration
    initSelfClose()
  }
)

function initSelfClose() {
  // Close after duration
  setTimeout(() => {
    close()
  }, props.duration)
}
</script>

<style lang="scss">
.sm-notification-item {
  width: 300px;
  background-color: var(--sm-cta);
  border-radius: var(--border-radius-base);
  color: var(--sm-white);
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: var(--sm-cta);
  }

  &__icon {
    flex: 0 0 2rem;
    margin-right: 1rem;
    margin-left: 1rem;
  }

  &__content {
    flex: 1 1 auto;
    margin-right: 1rem;
    display: flex;
    flex-direction: column;
    align-items: baseline;

    &__title {
      font-size: var(--font-size);
    }

    &__description {
      font-size: var(--font-size-small);
    }
  }

  &__action {
    flex: 0 0 2rem;
  }
}

.sm-notification-progress-bar {
  width: 100%;
  height: 5px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.5rem;

  &__fill {
    height: 100%;
    background-color: var(--sm-white);
    border-radius: 2px;
    transition: width 0.3s ease-in-out;
    animation: progress-pulse 1.5s ease-in-out infinite;
  }
}

.sm-notification-progress-bar__text {
  font-size: var(--font-size-small);
  margin-top: 0.25rem;
  text-align: center;
  opacity: 0.8;
}

.animation-spin {
  animation: spin 3s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes progress-pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}
</style>
